export type StreamingBashWrite = {
  stepDir: string;
  filePath: string;
  content: string;
};

export type BashFileTarget = {
  stepDir: string;
  filePath: string;
};

export type StreamingFileEdit = {
  stepDir: string;
  filePath: string;
  oldString: string;
  newString: string | null;
};

const HEREDOC_HEADER =
  /cat\s*>\s*("(?:[^"\\]|\\.)*"|'[^']*'|\S+)\s*<<-?\s*['"]?(\w+)['"]?\s*\n/g;

function unquote(path: string): string {
  if (
    (path.startsWith('"') && path.endsWith('"')) ||
    (path.startsWith("'") && path.endsWith("'"))
  ) {
    return path.slice(1, -1);
  }
  return path;
}

function toTarget(rawPath: string): BashFileTarget | null {
  const cleaned = unquote(rawPath).replace(/^\/+/, "");
  const slashIdx = cleaned.indexOf("/");
  if (slashIdx <= 0) return null;
  const stepDir = cleaned.slice(0, slashIdx);
  const filePath = cleaned.slice(slashIdx + 1);
  if (!filePath) return null;
  return { stepDir, filePath };
}

export function parseStreamingBashWrite(
  command: string
): StreamingBashWrite | null {
  let last: { path: string; token: string; bodyStart: number } | null = null;
  HEREDOC_HEADER.lastIndex = 0;
  for (;;) {
    const m = HEREDOC_HEADER.exec(command);
    if (m === null) break;
    last = {
      path: m[1]!,
      token: m[2]!,
      bodyStart: m.index + m[0].length,
    };
  }
  if (!last) return null;

  const rest = command.slice(last.bodyStart);
  const lines = rest.split("\n");
  let endIdx = lines.length;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i] === last.token) {
      endIdx = i;
      break;
    }
  }
  const content = lines.slice(0, endIdx).join("\n");

  const target = toTarget(last.path);
  if (!target) return null;
  return { ...target, content };
}

const QUOTED_OR_BARE = `("(?:[^"\\\\]|\\\\.)*"|'[^']*'|[^\\s|&;<>()"']+)`;

// Redirects: `> path` and `>> path` (excluding `2>`, `&>`, `>&`).
const REDIRECT = new RegExp(`(?<![0-9&])>>?\\s*${QUOTED_OR_BARE}`, "g");
// `tee [-a] path1 [path2 ...]` — captures the first path.
const TEE = new RegExp(
  `\\btee\\b(?:\\s+-[aA-Za-z]*)*\\s+${QUOTED_OR_BARE}`,
  "g"
);
// `sed -i ... <path>` — last positional arg is the file. We capture all
// remaining args after `-i` and pick the last non-flag-looking one.
const SED_INPLACE =
  /\bsed\b(?:\s+[^\n;|&]*?\s|\s+)-[a-zA-Z]*i[a-zA-Z]*\b([^\n;|&]*)/g;
// `mv|cp [flags] <src...> <dest>` — capture the trailing arg as the
// destination. Like sed, we pull the tail and pick the last token.
const MV_CP = /\b(?:mv|cp)\b([^\n;|&]*)/g;
// `cat|head|tail|less|more [flags] <path>` — capture the tail and pick the
// first non-flag token as the read source. Stops at any redirect operator so
// `cat > step-a/out` (a write) is left to HEREDOC/REDIRECT handling.
const READ_CMD = /\b(?:cat|head|tail|less|more)\b([^\n;|&]*)/g;

function tokenize(tail: string): string[] {
  const tokens: string[] = [];
  let i = 0;
  while (i < tail.length) {
    const ch = tail[i]!;
    if (ch === " " || ch === "\t") {
      i++;
      continue;
    }
    if (ch === '"' || ch === "'") {
      const quote = ch;
      let j = i + 1;
      while (j < tail.length && tail[j] !== quote) {
        if (tail[j] === "\\" && quote === '"') j++;
        j++;
      }
      tokens.push(tail.slice(i, Math.min(j + 1, tail.length)));
      i = j + 1;
      continue;
    }
    let j = i;
    while (j < tail.length && tail[j] !== " " && tail[j] !== "\t") j++;
    tokens.push(tail.slice(i, j));
    i = j;
  }
  return tokens;
}

function lastTokenAsTarget(tail: string): BashFileTarget | null {
  const tokens = tokenize(tail);
  for (let k = tokens.length - 1; k >= 0; k--) {
    const t = tokens[k]!;
    if (t.startsWith("-")) continue;
    return toTarget(t);
  }
  return null;
}

const REDIRECT_TOKEN = /^(?:\d*>>?|<<?|&>|>&)$/;

function firstTokenAsTarget(tail: string): BashFileTarget | null {
  const tokens = tokenize(tail);
  for (const t of tokens) {
    if (REDIRECT_TOKEN.test(t)) return null;
    if (t.startsWith("-")) continue;
    return toTarget(t);
  }
  return null;
}

export function parseBashFileTarget(command: string): BashFileTarget | null {
  let bestEnd = -1;
  let bestTarget: BashFileTarget | null = null;

  const consider = (target: BashFileTarget | null, end: number) => {
    if (!target) return;
    if (end > bestEnd) {
      bestEnd = end;
      bestTarget = target;
    }
  };

  HEREDOC_HEADER.lastIndex = 0;
  for (;;) {
    const m = HEREDOC_HEADER.exec(command);
    if (m === null) break;
    consider(toTarget(m[1]!), m.index);
  }

  REDIRECT.lastIndex = 0;
  for (;;) {
    const m = REDIRECT.exec(command);
    if (m === null) break;
    consider(toTarget(m[1]!), m.index);
  }

  TEE.lastIndex = 0;
  for (;;) {
    const m = TEE.exec(command);
    if (m === null) break;
    consider(toTarget(m[1]!), m.index);
  }

  SED_INPLACE.lastIndex = 0;
  for (;;) {
    const m = SED_INPLACE.exec(command);
    if (m === null) break;
    consider(lastTokenAsTarget(m[1]!), m.index);
  }

  MV_CP.lastIndex = 0;
  for (;;) {
    const m = MV_CP.exec(command);
    if (m === null) break;
    consider(lastTokenAsTarget(m[1]!), m.index);
  }

  READ_CMD.lastIndex = 0;
  for (;;) {
    const m = READ_CMD.exec(command);
    if (m === null) break;
    consider(firstTokenAsTarget(m[1]!), m.index);
  }

  return bestTarget;
}

type ToolInput = {
  command?: unknown;
  path?: unknown;
  content?: unknown;
  oldString?: unknown;
  newString?: unknown;
};

function isString(v: unknown): v is string {
  return typeof v === "string";
}

export function inputToFileTarget(
  input: ToolInput | undefined
): BashFileTarget | null {
  if (!input) return null;
  if (isString(input.command)) return parseBashFileTarget(input.command);
  if (isString(input.path)) return toTarget(input.path);
  return null;
}

export function inputToFileWrite(
  input: ToolInput | undefined
): StreamingBashWrite | null {
  if (!input) return null;
  if (isString(input.command)) return parseStreamingBashWrite(input.command);
  if (isString(input.path) && isString(input.content)) {
    const target = toTarget(input.path);
    if (!target) return null;
    return { ...target, content: input.content };
  }
  return null;
}

export function inputToFileEdit(
  input: ToolInput | undefined
): StreamingFileEdit | null {
  if (!input || !isString(input.path) || !isString(input.oldString)) {
    return null;
  }
  const target = toTarget(input.path);
  if (!target) return null;
  return {
    ...target,
    oldString: input.oldString,
    newString: isString(input.newString) ? input.newString : null,
  };
}
